import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Form, Media, Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Upload, PlusCircle, Edit, Trash2, UserX, UserPlus } from 'react-feather';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../Common/Modals/DeleteConfirmationModal';
import JobDataModal from './JobDataModal';
import { confirmDelete, fetchTableData, handleAddModifyOperation } from '../../../Api/ApiMethods';
import axios from 'axios';

const JobPool = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedJob, setSelectedJob] = useState(null);
    const [resetConfirmModalOpen, setResetConfirmModalOpen] = useState(false);
    const [jobToResetId, setJobToResetId] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [jobToAssignId, setJobToAssignId] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);

    const jobPoolApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool';
    const employeeApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/user';

    const fetchData = useCallback(() => {
        fetchTableData({
            apiEndpoint: jobPoolApiUrl,
            page,
            pageSize,
            search,
            setDataCallback: setData,
            setTotalRowsCallback: setTotalRows,
        });
    }, [jobPoolApiUrl, page, pageSize, search]);

    const fetchEmployees = useCallback(async () => {
        try {
            const response = await axios.get(`${employeeApiUrl}?userRole=callcenter`);
            let users = [];
            if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
                users = response.data.data.items;
            } else if (Array.isArray(response.data?.data)) {
                users = response.data.data;
            } else if (Array.isArray(response.data)) {
                users = response.data;
            } else {
                console.error("Unexpected employee list response structure:", response.data);
                toast.error("Personel listesi alınırken beklenmedik yanıt yapısı.");
                setEmployeeList([]);
                return;
            }

            const formattedUsers = users
                .filter(user => user.id && user.firstName && user.lastName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName}`
                }));

            setEmployeeList(formattedUsers);
        } catch (error) {
            console.error("Error fetching employees:", error);
            toast.error("Personel listesi alınırken bir hata oluştu.");
            setEmployeeList([]);
        }
    }, [employeeApiUrl]);

    useEffect(() => {
        fetchData();
        fetchEmployees();
    }, [fetchData, fetchEmployees]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const toggleModal = () => setModalOpen(!modalOpen);

    const toggleResetConfirmModal = (id = null) => {
        setJobToResetId(id);
        setResetConfirmModalOpen(!resetConfirmModalOpen);
    };

    const toggleAssignModal = (jobId = null) => {
        if (jobId) {
            setSelectedUserId('');
            setJobToAssignId(jobId);
        } else {
            setJobToAssignId(null);
        }
        setAssignModalOpen(!assignModalOpen);
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedJob(null);
        toggleModal();
    };

    const openModifyModal = (job) => {
        setModalMode('modify');
        setSelectedJob(job);
        toggleModal();
    };

    const handleResetUserAssignment = async () => {
        if (!jobToResetId) {
            toast.error("Sıfırlanacak iş ID bulunamadı.");
            toggleResetConfirmModal();
            return;
        }

        const resetEndpoint = `${jobPoolApiUrl}/reset-task/${jobToResetId}`;
        console.log("Calling reset endpoint (POST):", resetEndpoint);

        try {
            const response = await axios.post(resetEndpoint);

            console.log("Reset Response:", response);
            toast.success('Kullanıcı ataması başarıyla sıfırlandı!');
            fetchData();
        } catch (error) {
            console.error("Error resetting user assignment:", error.response?.data || error.message);
            toast.error(`Kullanıcı ataması sıfırlanırken hata oluştu: ${error.response?.data?.message || error.message}`);
        } finally {
            toggleResetConfirmModal();
        }
    };

    const handleAssignUser = async () => {
        if (!jobToAssignId) {
            toast.error("Atanacak iş ID bulunamadı.");
            return;
        }
        if (!selectedUserId) {
            toast.error("Lütfen atanacak bir personel seçin.");
            return;
        }

        const assignEndpoint = `${jobPoolApiUrl}/update-assignment/${jobToAssignId}`;
        const payload = { userId: selectedUserId };
        console.log("Calling assign endpoint (PUT):", assignEndpoint, "with payload:", payload);
        setIsAssigning(true);

        try {
            const response = await axios.put(assignEndpoint, payload);

            console.log("Assign Response:", response);
            toast.success('İş başarıyla personele atandı!');
            fetchData();
            toggleAssignModal();
        } catch (error) {
            console.error("Error assigning user:", error.response?.data || error.message);
            const errorMessage = error.response?.data?.errors?.userId?.[0] || error.response?.data?.message || error.message;
            toast.error(`İş atanırken hata oluştu: ${errorMessage}`);
        } finally {
            setIsAssigning(false);
        }
    };

    const onFileChange = async (event) => {
        if (event && event.target.files.length > 0) {
            const formData = new FormData();
            for (let i = 0; i < event.target.files.length; i++) {
                formData.append('files', event.target.files[i]);
            }

            event.target.value = null;

            try {
                toast.info('Dosyalar yükleniyor...');
                await handleAddModifyOperation({
                    apiEndpoint: jobPoolApiUrl,
                    mode: 'add',
                    data: formData,
                    refreshDataCallback: fetchData,
                    componentName: 'Dosyalar',
                });
            } catch (error) {
                console.error("File upload failed:", error);
            }

        } else {
            toast.error('Lütfen en az bir dosya seçin!');
        }
    };

    const onFileUpload = () => {
        document.getElementById('upfile').click();
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleSearch = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const columns = [
        {
            name: 'Atanan Kullanıcı',
            selector: row => {
                if (row.user && row.user.firstName && row.user.lastName) {
                    return `${row.user.firstName} ${row.user.lastName}`;
                }
                return 'Atanmamış';
            },
            sortable: true
        },
        { name: 'Plaka', selector: row => row.plate, sortable: true },
        { name: 'Şase Numarası', selector: row => row.chassisNumber, sortable: true },
        { name: 'Kaza Tarihi', selector: row => row.accidentDate ? new Date(row.accidentDate).toLocaleString() : '-', sortable: true },
        { name: 'Rapor Tarihi', selector: row => row.reportDate ? new Date(row.reportDate).toLocaleString() : '-', sortable: true },
        { name: 'TC Kimlik Numarası', selector: row => row.tcid, sortable: true },
        { name: 'Ad Soyad', selector: row => row.fullName, sortable: true },
        { name: 'Referans', selector: row => row.reference || '-', sortable: true },
        { name: 'Kaynak Dosyası', selector: row => row.originFolder || '-', sortable: true },
        { name: 'Telefon', selector: row => {
            if (Array.isArray(row.phone) && row.phone.length > 0) {
                const firstPhoneObj = row.phone[0];
                if (firstPhoneObj && typeof firstPhoneObj === 'object' && firstPhoneObj.hasOwnProperty('phone') && firstPhoneObj.phone) {
                    const firstPhone = firstPhoneObj.phone;
                    if (row.phone.length > 1) {
                        const validPhonesCount = row.phone.filter(p => p && p.phone && p.phone.trim() !== '').length;
                        if (validPhonesCount > 1) {
                            const remainingCount = validPhonesCount - 1;
                            return `${firstPhone} +${remainingCount}`;
                        }
                    }
                    return firstPhone;
                }
            }
            return '-';
        }, sortable: true },
        { name: 'Kusur Durumu', selector: row => (row.faultStatus !== null && row.faultStatus !== undefined ? `${row.faultStatus}%` : '-'), sortable: true },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            right: true,
            button: true
        },
        {
            name: 'Atama Yap',
            cell: (row) => (
                !row.user ? (
                    <div
                        className="btn btn-outline-success d-flex align-items-center justify-content-center"
                        onClick={() => toggleAssignModal(row.id)}
                        title="Personel Ata"
                    >
                        <UserPlus size={18} />
                    </div>
                ) : (
                    '-'
                )
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            right: true,
            button: true
        },
        {
            name: 'Atamayı Kaldır',
            cell: (row) => (
                row.user ? (
                    <div
                        className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                        onClick={() => toggleResetConfirmModal(row.id)}
                        title="Atamayı Kaldır"
                    >
                        <UserX size={18} />
                    </div>
                ) : (
                    '-'
                )
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            right: true,
            button: true
        },
    ];

    return (
        <Fragment>
            <Breadcrumbs parent='Yönetim Alanları' title='İş Havuzu' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>İş Havuzu</H5>
                                <span>Yeni çağrı formu oluşturmak için uygun olan kayıtlar aşağıda listelenmektedir.</span>
                                <input type="file" id="upfile" multiple style={{ display: 'none' }} onChange={onFileChange} accept=".xlsx, .xls, .csv, .txt" />
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={search}
                                                onChange={handleSearch}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <div className='btn btn-outline-success ms-2 mt-3' onClick={onFileUpload} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <Upload size={18} className="me-1"/>
                                        {' Dosya Yükle'}
                                    </div>
                                    <div className='btn btn-outline-primary ms-2 mt-3' onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1"/>
                                        {' İş Ekle'}
                                    </div>
                                    {(selectedRows.length !== 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1"/>
                                            {'Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>
                                <div style={{ height: "40px" }}></div>
                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        paginationServer
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={newPerPage => {
                                            setPageSize(newPerPage);
                                            setPage(1);
                                        }}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <JobDataModal
                apiEndpoint={jobPoolApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedJob={selectedJob}
                refreshData={fetchData}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="plate"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: jobPoolApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: fetchData,
                    })
                }
            />
            <Modal isOpen={resetConfirmModalOpen} toggle={() => toggleResetConfirmModal()} centered>
                <ModalHeader toggle={() => toggleResetConfirmModal()}>İş Atamasını Sıfırla</ModalHeader>
                <ModalBody>
                    Bu iş kaydının kullanıcı atamasını sıfırlamak istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={handleResetUserAssignment}>Evet, Sıfırla</Button>{' '}
                    <Button color="secondary" onClick={() => toggleResetConfirmModal()}>Hayır, İptal</Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={assignModalOpen} toggle={() => toggleAssignModal()} centered>
                <ModalHeader toggle={() => toggleAssignModal()}>Personel Atama</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="employeeSelect">Personel Seçin</Label>
                        <Input
                            type="select"
                            name="employeeSelect"
                            id="employeeSelect"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                        >
                            <option value="">-- Personel Seçiniz --</option>
                            {employeeList.length > 0 ? (
                                employeeList.map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                    </option>
                                ))
                            ) : (
                                <option value="" disabled>Personel listesi yükleniyor veya boş...</option>
                            )}
                        </Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAssignUser} disabled={!selectedUserId || isAssigning}>
                        {isAssigning ? 'Atanıyor...' : 'Ata'}
                    </Button>{' '}
                    <Button color="secondary" onClick={() => toggleAssignModal()} disabled={isAssigning}>İptal</Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default JobPool;
